import { LocalTableStore } from '@infra/persistence/LocalTableStore';
import { NexxusDatabase } from '@infra/persistence/NexxusDatabase';
import { UserRepository } from '@infra/persistence/UserRepository';
import { SqliteUserRepository } from '@infra/persistence/SqliteUserRepository';
import { SqliteLocalRepositoriesAdapter } from '@infra/persistence/adapters/SqliteLocalRepositoriesAdapter';
import type { IUserRepository } from '@infra/auth/AuthService';
import { AuthService } from '@infra/auth/AuthService';
import { TenantContext } from '@infra/tenancy/TenantContext';
import { logger } from '@infra/telemetry/Logger';
import type { DataMode } from '@infra/licence/ActivationClaims';
import type { LicenceRuntime } from '@infra/licence/LicenceRuntime';
import { resolveSignedDataMode, isRemoteMultiStationMode } from '@infra/licence/DataModeResolver';
import { RemoteAuthService } from '@application/auth/RemoteAuthService';
import { RepositoryFactory } from '@infra/persistence/adapters/RepositoryFactory';
import type { IPersistencePort } from '@infra/persistence/IPersistencePort';
import type { TenantScopedRepository } from '@infra/tenancy/TenantScopedRepository';
import {
  setEntityWriteRoleProvider,
  setEntityReadOnlyProvider,
} from '@infra/tenancy/entityWriteGuard';
import type { Salarie } from '@shared/types/Salarie';
import type { Bulletin } from '@shared/types/Bulletin';
import type { Ecriture } from '@shared/types/Ecriture';
import type { Facture } from '@shared/types/Facture';
import type { CentreAnalytique } from '@shared/types/CentreAnalytique';

/**
 * Conteneur d'injection de dépendances de l'application.
 * Singleton instancié au boot, fournit repositories scoppés tenant + services.
 */
export class AppContext {
  private static instance: AppContext | null = null;

  readonly db: NexxusDatabase;
  readonly localTables: LocalTableStore;
  /** Suit le stockage utilisateurs de l'authentification (Dexie, ou SQLite chiffré sur le poste Electron). */
  userRepo: IUserRepository;
  readonly auth: AuthService;
  readonly tenantContext: TenantContext;

  salaries!: TenantScopedRepository<Salarie>;
  bulletins!: TenantScopedRepository<Bulletin>;
  ecritures!: TenantScopedRepository<Ecriture>;
  factures!: TenantScopedRepository<Facture>;
  centresAnalytiques!: TenantScopedRepository<CentreAnalytique>;

  private licenceRuntime: LicenceRuntime | null = null;
  private lockedDataMode: DataMode | null = null;
  private persistenceBundle: IPersistencePort | null = null;
  private persistenceConfigured = false;

  private constructor() {
    this.db = NexxusDatabase.getInstance();
    this.localTables = new LocalTableStore(this.db);
    this.userRepo = new UserRepository(this.db);
    this.auth = new AuthService(this.userRepo);
    this.tenantContext = TenantContext.getInstance();
  }

  static getInstance(): AppContext {
    if (!this.instance) this.instance = new AppContext();
    setEntityWriteRoleProvider(() => this.instance?.auth.getSession()?.role);
    setEntityReadOnlyProvider(() => Boolean(this.instance?.licenceRuntime?.isReadOnly()));
    return this.instance;
  }

  /** Réinitialise le singleton (tests uniquement). */
  static resetForTests(): void {
    this.instance = null;
    setEntityWriteRoleProvider(() => undefined);
    setEntityReadOnlyProvider(() => false);
  }

  async init(): Promise<void> {
    await this.db.open();
    logger.info({ event: 'app_context_init' });
  }

  setLicenceRuntime(runtime: LicenceRuntime): void {
    this.licenceRuntime = runtime;
  }

  getLicenceRuntime(): LicenceRuntime | null {
    return this.licenceRuntime;
  }

  /** Mode de données verrouillé depuis le certificat signé (non modifiable renderer). */
  getLockedDataMode(): DataMode | null {
    return this.lockedDataMode;
  }

  getPersistenceBundle(): IPersistencePort | null {
    return this.persistenceBundle;
  }

  isRemoteOnline(): boolean {
    return this.persistenceBundle?.isRemoteOnline() ?? false;
  }

  /**
   * Configure l'adapter de persistance à partir du certificat d'activation.
   * Ne peut être appelé qu'une seule fois au démarrage.
   */
  async configurePersistenceFromLicence(): Promise<void> {
    if (this.persistenceConfigured) {
      throw new Error('persistence_already_configured');
    }
    await this.applyPersistenceFromClaims();
    this.persistenceConfigured = true;
    logger.info({ event: 'persistence_configured', dataMode: this.lockedDataMode });
  }

  /**
   * Rebind après switch Multi-entités (même mode Standalone ou Cloud).
   * Refuse un changement de data_mode (sécurité).
   */
  async rebindPersistenceForTenant(): Promise<void> {
    if (!this.persistenceConfigured) {
      throw new Error('persistence_not_configured');
    }
    const runtime = this.getLicenceRuntime();
    const claims = runtime?.getState()?.claims;
    const mode = resolveSignedDataMode(claims);
    if (this.lockedDataMode && this.lockedDataMode !== mode) {
      throw new Error('data_mode_switch_forbidden');
    }
    if (runtime && runtime.dataMode !== mode) {
      throw new Error('data_mode_tamper_detected');
    }
    await this.applyPersistenceFromClaims();
    logger.info({
      event: 'persistence_rebound',
      dataMode: mode,
      tenantId: claims?.tenant_id,
    });
  }

  private async applyPersistenceFromClaims(): Promise<void> {
    const runtime = this.getLicenceRuntime();
    const claims = runtime?.getState()?.claims;
    const mode = resolveSignedDataMode(claims);
    if (runtime && runtime.dataMode !== mode) {
      throw new Error('data_mode_tamper_detected');
    }
    const bundle = await RepositoryFactory.create(mode, this.db, claims?.tenant_id ?? 'default');
    if (await SqliteLocalRepositoriesAdapter.isActive()) {
      const sqliteUsers = new SqliteUserRepository();
      this.auth.useUserRepository(sqliteUsers);
      this.userRepo = sqliteUsers;
    }
    this.salaries = bundle.salaries;
    this.bulletins = bundle.bulletins;
    this.ecritures = bundle.ecritures;
    this.factures = bundle.factures;
    this.centresAnalytiques = bundle.centresAnalytiques;
    this.lockedDataMode = mode;
    this.persistenceBundle = bundle;
    if (isRemoteMultiStationMode(mode)) {
      const remoteAuth = new RemoteAuthService();
      this.auth.setRemoteSessionRefresh(() => remoteAuth.refreshSessionFromServer());
    } else {
      this.auth.setRemoteSessionRefresh(null);
    }
  }
}
